import React, { useEffect } from "react";
import { ActionButton } from "../atoms/ActionButton";
import { LoadingSpinner } from "../atoms/LoadingSpinner";
import { GoInfo } from "react-icons/go";


export interface PaginatedSolutionsSectionProps {
    solutions: string[][];
    pageNumber: number;
    setPageNumber: (page: number) => void;
    isLoading?: boolean;
    includeNumbers?: boolean;
    useContinuousNumbers?: boolean;
}

export const PaginatedSolutionsSection: React.FC<PaginatedSolutionsSectionProps> = ({
    solutions,
    pageNumber,
    setPageNumber,
    isLoading = false,
    includeNumbers = false,
    useContinuousNumbers = false
}) => {

    const getAbsolutePositionInSolutions = (page: number, index: number) => {
        let absolutePosition = 0;
        for (let i = 0; i < page; i++) {
            absolutePosition += solutions[i].length;
        }
        return absolutePosition + index + 1;
    }

    return (
        <div className="min-w-max flex flex-col w-full border-2 border-brd-muted rounded-lg items-center">
            <h2 className="rounded-t text-lg font-bold text-center p-2 bg-primary-highlight text-text-light w-full">
                {isLoading
                    ? "Checking..."
                    : `Potential Solutions (${solutions.reduce((total, subArray) => total + subArray.length, 0)})`}
            </h2>
            {isLoading
                ? (
                    <div className="p-10">
                        <LoadingSpinner />
                    </div>
                ) : (solutions.length > 0
                    ? <>
                        <div className="w-full gap-4 flex flex-col px-2 py-4 justify-start items-center">
                            {solutions[pageNumber].map((word, index) => (
                                <div className="flex items-center" key={`${pageNumber}-${index}`}>
                                    {includeNumbers && (
                                        <div className="font-bold rounded-s-lg bg-primary-base text-text-contrast text-center py-4 pl-6 pr-2">
                                            {useContinuousNumbers
                                                ? getAbsolutePositionInSolutions(pageNumber, index)
                                                : index + 1}
                                        </div>
                                    )}
                                    <div className={`font-bold bg-primary-base p-4 text-text-light min-w-max text-center tracking-widest ${includeNumbers ? "rounded-e-lg" : "rounded-lg"}`}>
                                        {word.toUpperCase()}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <PageButtons
                            pageNumber={pageNumber}
                            setPageNumber={setPageNumber}
                            totalPages={solutions.length}
                        />
                    </>
                    : <NoSolutions />
                )
            }
        </div>
    );
}


export interface PageButtonsProps {
    pageNumber: number;
    setPageNumber: (page: number) => void;
    totalPages: number;
}

export const PageButtons: React.FC<PageButtonsProps> = ({
    pageNumber,
    setPageNumber,
    totalPages
}) => {
    const previousActive = pageNumber > 0;
    const nextActive = pageNumber < totalPages - 1;
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement;

            // Skip if the user is focused on an input or textarea
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
                return;
            }

            if (event.key === "ArrowRight" && nextActive) {
                setPageNumber(pageNumber + 1);
            } else if (event.key === "ArrowLeft" && previousActive) {
                setPageNumber(pageNumber - 1);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [pageNumber, setPageNumber, totalPages, previousActive, nextActive]);

    return (
        <div className="w-full flex flex-row py-2 px-6 gap-6 text-text-light">
            <ActionButton
                disabled={!previousActive}
                onClick={() => setPageNumber(pageNumber - 1)}
                label="< Previous"
                className="w-full"
            />
            <ActionButton
                disabled={!nextActive}
                onClick={() => setPageNumber(pageNumber + 1)}
                label="Next >"
                className="w-full"
            />
        </div>
    );
}


export const NoSolutions = () => {
    return (
        <div className="flex gap-2 items-center justify-center w-full h-40 bg-background-muted rounded-b-md shadow-md text-text-base">
            <GoInfo />
            <span className="text-xl text-text-muted font-semibold">
                No solutions found!
            </span>
        </div>
    );
}